import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, Camera, Loader2, ImageOff, ArrowUp, ArrowDown, HandHeart } from 'lucide-react';
import { PageHeader } from './MassTimes';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { AddButton, CardControls } from '../components/AdminControls';
import EditDialog from '../components/EditDialog';
import ConfirmDialog from '../components/ConfirmDialog';

const MINISTRY_FIELDS = [
  { name: 'name', label: 'Name', required: true },
  { name: 'slug', label: 'Slug (URL-safe id)', required: true, placeholder: 'e.g. altar-servers' },
  { name: 'group', label: 'Group', type: 'select', required: true, options: [
    { value: 'ministries', label: 'Ministries' },
    { value: 'programs', label: 'Programs' },
  ]},
  { name: 'category', label: 'Category', type: 'select', required: true, options: [
    { value: 'Liturgical', label: 'Liturgical' },
    { value: 'Community', label: 'Community' },
    { value: 'Faith Formation', label: 'Faith Formation' },
    { value: 'Outreach', label: 'Outreach' },
  ]},
  { name: 'description', label: 'Short description (shown on the detail page)', type: 'textarea', required: true, rows: 3 },
  { name: 'video_url', label: 'Video URL (optional — YouTube or Vimeo, shown beside the text on the detail page)', placeholder: 'https://youtu.be/xxxxxxxxxxx', help: 'Paste a YouTube or Vimeo URL to show a video instead of (or below) the photo.' },
];

// Single tile: image on top, title below, whole card is a link.
const MinistryTile = ({
  ministry,
  isAdmin,
  uploading,
  onEdit,
  onDelete,
  onFileChosen,
  onRemoveImage,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  const inputRef = useRef(null);
  const src = api.ministryImageAbs(ministry.image_url);
  const detailPath = `/parish-life/ministries/${encodeURIComponent(ministry.slug)}`;

  return (
    <article
      className="relative group bg-[#3a2359] border border-[#3a2359] hover:shadow-md hover:-translate-y-0.5 overflow-hidden flex flex-col"
      data-testid={`ministry-tile-${ministry.slug}`}
    >
      {/* Image on top — clickable */}
      <Link to={detailPath} className="block relative w-full aspect-[19/10] bg-[#f2ead6] overflow-hidden">
        {src ? (
          <img
            src={src}
            alt={ministry.name}
            className="w-full h-full object-cover group-hover:scale-[1.03]"
            style={{ transition: 'transform 700ms ease' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#a68a5b]">
            <HandHeart size={24} strokeWidth={1.6} />
          </div>
        )}
        {isAdmin && (
          <div className="absolute top-2 left-2 flex gap-2" onClick={(e) => e.preventDefault()}>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); inputRef.current?.click(); }}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#3a2359] text-[11px] font-medium shadow disabled:opacity-70"
              data-testid={`ministry-change-photo-${ministry.slug}`}
            >
              {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Camera size={12} /> {src ? 'Change' : 'Add photo'}</>}
            </button>
            {src && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveImage && onRemoveImage(ministry); }}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#8a2b1a] text-[11px] font-medium shadow disabled:opacity-70"
                data-testid={`ministry-remove-photo-${ministry.slug}`}
              >
                <ImageOff size={12} /> Remove
              </button>
            )}
          </div>
        )}
      </Link>

      {/* Text below the image */}
      <div className="px-5 py-4 flex-1 flex flex-col">
        {isAdmin && (onEdit || onDelete) && (
          <CardControls onEdit={onEdit} onDelete={onDelete} />
        )}
        <Link to={detailPath} className="block">
          <h3 className="font-display text-lg md:text-xl text-white leading-snug group-hover:text-[#e6d4a8]">
            {ministry.name}
          </h3>
        </Link>
        {isAdmin && (onMoveUp || onMoveDown) && (
          <div className="mt-3 pt-3 border-t border-white/20 flex items-center justify-between gap-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#e6d4a8] font-medium">Reorder</span>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMoveUp && onMoveUp(ministry); }}
                disabled={!canMoveUp}
                data-testid={`ministry-move-up-${ministry.slug}`}
                aria-label="Move up"
                className="inline-flex items-center justify-center w-7 h-7 border border-white text-white bg-transparent hover:bg-white hover:text-[#3a2359] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white disabled:cursor-not-allowed"
              >
                <ArrowUp size={12} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMoveDown && onMoveDown(ministry); }}
                disabled={!canMoveDown}
                data-testid={`ministry-move-down-${ministry.slug}`}
                aria-label="Move down"
                className="inline-flex items-center justify-center w-7 h-7 border border-white text-white bg-transparent hover:bg-white hover:text-[#3a2359] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white disabled:cursor-not-allowed"
              >
                <ArrowDown size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {isAdmin && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && onFileChosen) onFileChosen(ministry, f);
            if (inputRef.current) inputRef.current.value = '';
          }}
          data-testid={`ministry-file-input-${ministry.slug}`}
        />
      )}
    </article>
  );
};

const Ministries = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.listMinistries();
      setItems(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (values) => {
    if (editing?.mode === 'new') {
      const { data } = await api.createMinistry(values);
      setItems(prev => [...prev, data]);
      toast({ title: values.group === 'programs' ? 'Program added' : 'Ministry added' });
    } else {
      const { data } = await api.updateMinistry(editing.id, values);
      setItems(prev => prev.map(x => x.id === data.id ? data : x));
      toast({ title: 'Updated' });
    }
  };

  const handleDelete = async () => {
    await api.deleteMinistry(deleteTarget.id);
    setItems(prev => prev.filter(x => x.id !== deleteTarget.id));
    toast({ title: deleteTarget?.group === 'programs' ? 'Program deleted' : 'Ministry deleted' });
    setDeleteTarget(null);
  };

  const handleFileChosen = async (m, file) => {
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      toast({ title: 'Please choose a JPEG, PNG, or WebP image', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Image is larger than 10 MB', variant: 'destructive' });
      return;
    }
    setUploadingId(m.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadMinistryImage(m.id, fd);
      setItems(prev => prev.map(x => x.id === data.id ? data : x));
      toast({ title: 'Photo uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemoveImage = async (m) => {
    setUploadingId(m.id);
    try {
      const { data } = await api.deleteMinistryImage(m.id);
      setItems(prev => prev.map(x => x.id === data.id ? data : x));
      toast({ title: 'Photo removed' });
    } catch (e) {
      toast({ title: 'Remove failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const move = async (m, direction) => {
    const idx = items.findIndex(x => x.id === m.id);
    if (idx < 0) return;
    const swap = direction === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      const { data } = await api.reorderMinistries(next.map(x => x.id));
      setItems(data);
    } catch (e) {
      toast({ title: 'Reorder failed', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
      setItems(items);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Get Involved"
        title="Ministries & Programs"
        subtitle="Discover the many ways you can grow in faith and serve at All Saints. Every gift and every calling has a place in our parish."
      />

      <section className="max-w-6xl mx-auto px-6 py-14">
        {loading ? (
          <p className="text-center text-[#78716c] py-8">Loading…</p>
        ) : (
          (() => {
            const ministries = items.filter(x => (x.group || 'ministries') === 'ministries');
            const programs = items.filter(x => x.group === 'programs');

            const renderGroup = (list) => (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {list.map((m) => {
                  const globalIdx = items.findIndex(x => x.id === m.id);
                  return (
                    <MinistryTile
                      key={m.id}
                      ministry={m}
                      isAdmin={isAdmin}
                      uploading={uploadingId === m.id}
                      onEdit={() => setEditing(m)}
                      onDelete={() => setDeleteTarget(m)}
                      onFileChosen={handleFileChosen}
                      onRemoveImage={handleRemoveImage}
                      onMoveUp={(x) => move(x, 'up')}
                      onMoveDown={(x) => move(x, 'down')}
                      canMoveUp={globalIdx > 0}
                      canMoveDown={globalIdx < items.length - 1}
                    />
                  );
                })}
              </div>
            );

            const sectionHeader = (title, addLabel, addGroup) => (
              <div className="flex items-end justify-between gap-3 mb-6">
                <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45]">{title}</h2>
                {isAdmin && (
                  <AddButton
                    onClick={() => setEditing({ mode: 'new', group: addGroup })}
                    label={addLabel}
                  />
                )}
              </div>
            );

            const emptyState = (kind) => (
              <p className="text-sm text-[#78716c] py-6">
                {isAdmin
                  ? `No ${kind} yet — click "Add ${kind === 'ministries' ? 'Ministry' : 'Program'}" above to create the first one.`
                  : 'More coming soon.'}
              </p>
            );

            const showMinistries = isAdmin || ministries.length > 0;
            const showPrograms = isAdmin || programs.length > 0;

            return (
              <>
                {showMinistries && (
                  <div data-testid="ministries-section" className="mb-14">
                    {sectionHeader('Ministries', 'Add Ministry', 'ministries')}
                    {ministries.length > 0 ? renderGroup(ministries) : emptyState('ministries')}
                  </div>
                )}
                {showPrograms && (
                  <div data-testid="programs-section">
                    {sectionHeader('Programs', 'Add Program', 'programs')}
                    {programs.length > 0 ? renderGroup(programs) : emptyState('programs')}
                  </div>
                )}
                {!showMinistries && !showPrograms && (
                  <p className="text-center text-[#78716c] py-8">More coming soon.</p>
                )}
              </>
            );
          })()
        )}

        <div className="mt-14 bg-[#f2ead6]/60 border border-[#e9e0cc] p-10 text-center">
          <h3 className="font-display text-3xl text-[#2a1a45] mb-3">Ready to Join a Ministry?</h3>
          <p className="text-[#5a534a] max-w-xl mx-auto mb-6">
            Speak with a member of our parish staff after Mass, or contact the parish office. We would love to help you find where God is calling you.
          </p>
          <a href="/contact" data-testid="ministries-contact-btn" className="inline-block px-6 py-3 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]">Contact the Parish Office</a>
        </div>

        <Link
          to="/parish-life/calendar"
          className="mt-6 flex items-center justify-between gap-4 bg-white border border-[#e9e0cc] hover:border-[#3a2359] p-6 group"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center group-hover:bg-[#3a2359] group-hover:text-[#e6d4a8]">
              <Calendar size={18} />
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b]">See What's Coming Up</p>
              <h4 className="font-display text-xl text-[#2a1a45] group-hover:text-[#3a2359]">View Parish Calendar</h4>
            </div>
          </div>
          <ChevronRight size={20} className="text-[#3a2359]" />
        </Link>
      </section>

      <EditDialog
        open={!!editing}
        title={editing?.mode === 'new' ? (editing?.group === 'programs' ? 'Add Program' : 'Add Ministry') : 'Edit Ministry'}
        fields={MINISTRY_FIELDS}
        initial={editing?.mode === 'new'
          ? { name: '', slug: '', group: editing?.group || 'ministries', category: '', description: '', video_url: '' }
          : (editing || {})}
        onClose={() => setEditing(null)}
        onSubmit={handleSave}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this ministry?"
        message={deleteTarget ? `“${deleteTarget.name}” will be permanently removed.` : ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default Ministries;
